import { Lead } from '../../services/leads.service'

interface LeadCardProps {
  lead: Lead
  selected: boolean
  onSelect: () => void
  onApprove: () => void
  onReject: () => void
  onViewDetails: () => void
}

export default function LeadCard({
  lead,
  selected,
  onSelect,
  onApprove,
  onReject,
  onViewDetails,
}: LeadCardProps) {
  const statusColors: Record<string, string> = {
    pending_review: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    message_generated: 'bg-blue-100 text-blue-800',
    sent: 'bg-purple-100 text-purple-800',
    replied: 'bg-emerald-100 text-emerald-800',
  }

  const statusLabels: Record<string, string> = {
    pending_review: 'На рассмотрении',
    approved: 'Одобрен',
    rejected: 'Отклонён',
    message_generated: 'Сообщение готово',
    sent: 'Отправлено',
    replied: 'Получен ответ',
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div
      className={`card border-2 transition-colors ${
        selected ? 'border-blue-500' : 'border-transparent'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          className="h-5 w-5 text-blue-600 rounded border-gray-300 mt-1"
        />

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {lead.username ? (
                <a
                  href={`https://t.me/${lead.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:underline"
                >
                  @{lead.username}
                </a>
              ) : (
                <span className="font-medium text-gray-900">
                  ID: {lead.telegram_user_id}
                </span>
              )}
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${
                  statusColors[lead.status] || 'bg-gray-100 text-gray-800'
                }`}
              >
                {statusLabels[lead.status] || lead.status}
              </span>
            </div>
            {lead.confidence !== null && (
              <span
                className={`text-sm font-medium ${
                  lead.confidence >= 80
                    ? 'text-green-600'
                    : lead.confidence >= 50
                    ? 'text-yellow-600'
                    : 'text-red-600'
                }`}
              >
                {lead.confidence}%
              </span>
            )}
          </div>

          {/* Rule badge */}
          {lead.rule_name && (
            <span className="inline-block px-2 py-0.5 text-xs rounded bg-indigo-50 text-indigo-700 mb-2">
              {lead.rule_name}
            </span>
          )}

          {/* Message */}
          <p className="text-gray-700 text-sm line-clamp-3 mb-2">
            {lead.message_text}
          </p>

          {/* Need Description */}
          {lead.need_description && (
            <p className="text-sm text-gray-500 italic mb-2">
              💡 {lead.need_description}
            </p>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{formatDate(lead.created_at)}</span>
            <div className="flex gap-2">
              {lead.status === 'pending_review' && (
                <>
                  <button
                    onClick={onApprove}
                    className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                  >
                    ✓ Одобрить
                  </button>
                  <button
                    onClick={onReject}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                  >
                    ✗ Отклонить
                  </button>
                </>
              )}
              <button
                onClick={onViewDetails}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Подробнее
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
